'use client';

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

import {
  releaseScrollPadding,
  reserveScrollPadding,
} from '@highereducation/hen-ui';

import {
  HEADER_TRANSITION_DURATION_VAR,
  HEADER_VISIBLE_HEIGHT_VAR,
} from '@hen-site/app/hooks/useHideOnScroll';

/**
 * Vertical gap between the site header and the docked TocNav (in
 * pixels). Intentionally `0` — the design spec for the horizontal TOC
 * bar (Figma "Sticky TOC - D", node 4430:52861) docks the bar
 * immediately under the navbar with no visible gutter, and the bar
 * itself carries `py-md` on its inner controls row so the visual
 * breathing room comes from the nav padding, not from the sticky
 * offset. The legacy vertical sidebar TOC used a 16px gap because it
 * sat next to the article body, not under the global header.
 *
 * If the design changes, override this constant rather than introducing
 * a per-template prop — the offset is global and should stay consistent
 * across `article` and `article-sidebar` templates.
 */
const HEADER_GAP_PX = 0;

const hideOnScrollHeaderStyle: CSSProperties = {
  top: `calc(var(--hen-admin-toolbar-height, 0px) + var(${HEADER_VISIBLE_HEIGHT_VAR}, 0px) + ${HEADER_GAP_PX}px)`,
  transitionProperty: 'top',
  transitionDuration: `var(${HEADER_TRANSITION_DURATION_VAR}, 0ms)`,
  transitionTimingFunction: 'ease-out',
};

type StickyTocNavProps = {
  children: ReactNode;
  className?: string;
};

/**
 * Pins the horizontal `<TocNav>` to the top of the viewport as the user
 * scrolls. Sticky on every viewport (the bar is horizontal — no
 * stacked/side-by-side breakpoint to gate on).
 *
 * The `top` offset is computed against any `[data-site-header]` element
 * on the page: if that header is itself `sticky` or `fixed`, its live
 * height is used so the TocNav docks immediately under the navbar;
 * otherwise the bar pins to `top: 0`. A `ResizeObserver` keeps the
 * offset in sync when the header height changes (mobile menu toggles,
 * responsive breakpoints).
 *
 * This mirrors the pattern in the (now-removed) `StickyTocWrapper`,
 * minus the `xl:` gate — the horizontal `<TocNav>` is responsive at
 * every width and should pin at every breakpoint.
 *
 * When the header is a `HideOnScrollHeader` (`data-hide-on-scroll`), the
 * bar docks under the header's *visible* height, published as a CSS
 * variable, and animates `top` with the header's reveal transition so the
 * two move as one.
 *
 * The bar also reserves `scroll-padding-top` on `<html>`, through hen-ui's
 * shared reservation so a docked StickyBar on the same page doesn't
 * overwrite it, and in-page jumps (TOC links) land below it. With a
 * hide-on-scroll header the header is kept hidden during those jumps, so
 * only the bar is padded for.
 */
export default function StickyTocNav({
  children,
  className,
}: StickyTocNavProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [topOffset, setTopOffset] = useState(HEADER_GAP_PX);
  const [followsHideOnScrollHeader, setFollowsHideOnScrollHeader] =
    useState(false);
  const [tocHeight, setTocHeight] = useState(0);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof ResizeObserver === 'undefined') return;
    const measureToc = () =>
      setTocHeight(wrapper.getBoundingClientRect().height);
    measureToc();
    const observer = new ResizeObserver(measureToc);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const root =
      (document.scrollingElement as HTMLElement | null) ??
      document.documentElement;
    reserveScrollPadding(
      root,
      wrapperRef,
      followsHideOnScrollHeader
        ? `calc(var(--hen-admin-toolbar-height, 0px) + ${tocHeight}px)`
        : topOffset + tocHeight,
    );
    return () => releaseScrollPadding(root, wrapperRef);
  }, [followsHideOnScrollHeader, topOffset, tocHeight]);

  useEffect(() => {
    const header = document.querySelector<HTMLElement>('[data-site-header]');
    if (header?.hasAttribute('data-hide-on-scroll')) {
      setFollowsHideOnScrollHeader(true);
      return;
    }

    const readToolbarValue = () =>
      window
        .getComputedStyle(document.documentElement)
        .getPropertyValue('--hen-admin-toolbar-height');
    let toolbarValue = readToolbarValue();

    const measure = () => {
      toolbarValue = readToolbarValue();
      const toolbarHeight = Number.parseFloat(toolbarValue) || 0;
      const headerPosition = header
        ? window.getComputedStyle(header).position
        : undefined;
      const headerHeight =
        headerPosition === 'sticky' || headerPosition === 'fixed'
          ? header?.getBoundingClientRect().height ?? 0
          : 0;

      setTopOffset(toolbarHeight + headerHeight + HEADER_GAP_PX);
    };

    measure();

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(measure);
    if (header) {
      resizeObserver?.observe(header);
    }
    // `<html>` style also carries this component's own `scroll-padding-top`;
    // only a toolbar height change should trigger a re-measure.
    const styleObserver = new MutationObserver(() => {
      if (readToolbarValue() !== toolbarValue) measure();
    });
    styleObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });
    window.addEventListener('resize', measure);

    return () => {
      resizeObserver?.disconnect();
      styleObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      data-follows-hide-on-scroll-header={
        followsHideOnScrollHeader ? '' : undefined
      }
      className={['sticky z-40', className].filter(Boolean).join(' ')}
      style={
        followsHideOnScrollHeader ? hideOnScrollHeaderStyle : { top: topOffset }
      }
    >
      {children}
    </div>
  );
}
